<template lang="html">
  <v-layout :column="isMobile">
    <v-repeat-load-group-dialog v-if="currentLoad"
                                v-model="showCopyLoadDialog"
                                :loadGroupId="currentLoad.humanId"
                                :sponsor="loadGroupSponsorName(currentLoad)"
                                :origin="currentLoad.loadGroup.details | originName"
                                :destination="currentLoad.loadGroup.details | destinationName"
                                :count="currentLoad.loadCount || 1"
                                :isMobile="isMobile"
                                :loading="loadingBtn"
                                :company="company"
                                :load-group="currentLoad.loadGroup"
                                @cancel="closeCopyLoadDialog"
                                @newLoad="closeCopyLoadDialog($event)">
    </v-repeat-load-group-dialog>

    <edit-nickname-dialog v-model="showNicknameModal"
                          @refresh="refreshLoad"
                          :load="currentLoad">
    </edit-nickname-dialog>

    <template v-if="selectedLoad">
      <toolbar :title="`Repeating Load: ${currentLoad.humanId}`">
        <v-icon @click="onRefresh()">refresh</v-icon>
      </toolbar>

      <v-breadcrumbs :items="breadcrumbItems">
      </v-breadcrumbs>
    </template>

    <v-master-detail-pane :selected="currentLoad" @reset="selectCurrentLoad(null)">
      <template slot="master">
        <v-load-nav-list search-prompt="Search Repeating Loads"
                         :title="`Repeating (${repeatingLoads.length})`"
                         :accurate-since="loadTemplatesAccurateSince"
                         :loads="repeatingLoads"
                         :customer-loads="groupedBySponsorLoads"
                         :loading="loadingLoadTemplates"

                         @update:searchTerm="search = $event"
                         @refresh="getLoadTemplates">
          <template slot="subheader">
          </template>

          <template slot="loadTile"
                    slot-scope="{ idx, load }">
            <v-list-tile :key="`unassigned_load_${idx}_${load.humanId}`"
                         @click="selectCurrentLoad(load)"
                         :class="{'primary lighten-1': isSelectedLoad(load)}"
                         :inactive="isSelectedLoad(load)"
                         ripple avatar>
              <v-list-tile-content>
                <v-list-tile-title v-if="_.get(load, 'nickname', false)">
                  {{load.nickname}}
                </v-list-tile-title>
                <v-list-tile-title v-else>{{ loadGroupSponsorName(load) }}</v-list-tile-title>
                <v-list-tile-sub-title class="text--primary">
                  {{loadGroupPickupLocation(load).googlePlace.region}}
                  to {{loadGroupDropoffLocation(load).googlePlace.region}}
                </v-list-tile-sub-title>
                <v-list-tile-sub-title>
                  {{load.loadCount}} trucks, {{ distanceLabel(load.loadedMiles) }}
                  <span v-if="load.billableAmount">, ${{ load.billableAmount }}</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>

              <v-list-tile-avatar class="pa-0"
                                  v-if="isProducerWithPermissions">
                <v-btn color="accent" fab flat small
                       class="ma-0"
                       @click.stop.prevent="openCopyLoadDialog(load)">
                  <v-icon>redo</v-icon>
                </v-btn>
              </v-list-tile-avatar>
            </v-list-tile>
            <v-divider :key="`unassigned_divider_${idx}_${load.listingHumanId}`"/>
          </template>

          <template slot="customerLoadsTile" slot-scope="skope">
            <v-list-group :key="`groupedBy_${skope.idx}`">
              <v-list-tile slot="activator">
                <v-list-tile-action>
                  <v-chip color="primary"
                          text-color="white" small>
                    {{ skope.loads.length }}
                  </v-chip>
                </v-list-tile-action>
                <v-list-tile-title>{{ skope.sponsor }}</v-list-tile-title>
              </v-list-tile>
              <template v-for="load in skope.loads" >
                <v-list-tile :key="`grouped_load_${load.humanId}`"
                             @click="selectCurrentLoad(load)"
                             :class="{'primary lighten-1': isSelectedLoad(load)}"
                             :inactive="isSelectedLoad(load)"
                             ripple>

                  <v-list-tile-content>
                    <v-list-tile-title v-if="load.nickname">
                      {{ load.nickname }}
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{loadGroupPickupLocation(load).googlePlace.region}}
                      to {{loadGroupDropoffLocation(load).googlePlace.region}}
                    </v-list-tile-sub-title>
                    <v-list-tile-sub-title>
                      {{load.loadCount}} trucks, {{ distanceLabel(load.loadGroup.details.loadedMiles) }}
                      <span v-if="_.get(load, 'loadGroup.amount', false)">, ${{ load.loadGroup.amount }}</span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                </v-list-tile>
                <v-divider :key="`load_divider_${load.humanId}`"/>
              </template>
            </v-list-group>
          </template>
        </v-load-nav-list>
      </template>

      <template slot="detail">
        <v-layout column>
          <v-load-group-details repeating
                                :read-only="!allowEdition"
                                @onFormChange="editionFormChange($event)"
                                @savingLoad="updateSavingState($event)"
                                @updateLoad="updateLoad($event)"
                                @reload="refreshLoad()"
                                :planning-load="currentLoad"
                                :company="company"
                                banner-title="The Repeating Load Tool"
                                :loading="loadingBtn">

            <span slot="notSelected">
              Here you'll find loads offered by other carriers and {{$t('booked-load.producers', 2)}} in your Preferred Carriers.
            </span>

            <template slot="toolbarButtons">
              <template v-if="allowEdition">
                <v-btn flat color="accent"
                       :disabled="savingLoad"
                       @click="toggleEdition">
                  <v-layout column>
                    <v-icon>clear</v-icon>
                    Ignore
                  </v-layout>
                </v-btn>
                <v-btn color="accent"
                       :loading="savingLoad"
                       @click="saveDetailsUpdates">
                  <v-layout column>
                    <v-icon>save</v-icon>
                    Save
                  </v-layout>
                </v-btn>
              </template>
              <v-btn v-else
                     flat color="accent"
                     @click="toggleEdition">
                <v-layout column>
                  <v-icon>edit</v-icon>
                  Edit
                </v-layout>
              </v-btn>

              <v-btn flat color="accent" @click.stop.prevent="openNicknameModal">
                <v-layout column>
                  <v-icon>vpn_key</v-icon>
                  Nickname
                </v-layout>
              </v-btn>
              <v-btn flat color="accent"  @click.stop.prevent="remove(currentLoad)">
                <v-layout column>
                  <v-icon>delete</v-icon>
                  Delete
                </v-layout>
              </v-btn>
              <v-btn flat color="accent" @click="openCopyLoadDialog(null)">
              <v-layout column>
                <v-icon>redo</v-icon>
                Repeat
              </v-layout>
              </v-btn>
            </template>
          </v-load-group-details>
        </v-layout>
      </template>
    </v-master-detail-pane>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import Vue2Filters from 'vue2-filters';
import Toolbar from '@/components/Toolbar';
import moment from 'moment';
import { mapGetters } from 'vuex';
import companyNamesMixin from '@/mixins/companyNames';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import filterLoads from '@/mixins/filterLoads';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import refreshPageMixin from '@/mixins/refreshPageMixin';
import loadsRoutingMixin from '@/mixins/loadsRoutingMixin';
import companySectionMixin from '@/mixins/companySectionMixin';
import IconBase from '@/components/IconBase';
import CompanyName from '@/components/CompanyName';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconCold from '@/components/icons/IconCold';
import LoadMap from '@/components/LoadMap';
import VFloatField from '@/components/VFloatField';
import VLoadNavList from '@/components/dashboard/simple/LoadNavList';
import * as types from '@/store/mutationTypes';
import * as Checks from '@/store/permissions';

import VLoadGroupDetails from '@/components/dashboard/simple/LoadGroupDetails';
import VEditLoadDetails from '@/components/InfoWidgets/EditLoadDetails';
import VMasterDetailPane from '@/components/MasterDetailPane';
import VRepeatLoadGroupDialog from '@/components/RepeatLoadGroupDialog';
import EditNicknameDialog from '@/components/EditNicknameDialog';

import VBooked from './Booked';
import VDispatchRequest from './DispatchRequest';

import unitsOfMeasurePlugin from '../../../../mixins/unitsOfMeasurePlugin';

export default {
  name: 'VRepeatLoadTemplatesTool',
  mixins: [
    companyNamesMixin,
    filterLoads,
    bookedLoadMixin,
    refreshPageMixin,
    loadGroupMixin,
    loadsRoutingMixin,
    companySectionMixin,
    Vue2Filters.mixin,
    unitsOfMeasurePlugin
  ],
  watch: {
    currentLoad(newVal) {
      this.$router.push({
        params: {
          repeatLoadId: _.get(newVal, 'humanId', null),
        },
      });
    },
  },
  data: () => ({
    selectedLoad: null,
    allowEdition: false,
    savingLoad: false,
    showNicknameModal: false,
    showCopyLoadDialog: false,
    currentLoad: null,

    sortDesc: false,
    loadingBtn: false,
    loadingLoad: false,
    assignTruckCount: null,

    selectedMembers: [],

    showCanceledNetworkRequests: false,
    planingDetailTab: null,

    showPlanningLoadsList: true,
    showPlanningRepeatList: false,

    isFormDirty: false,
  }),
  components: {
    EditNicknameDialog,
    VLoadGroupDetails,
    VMasterDetailPane,
    VRepeatLoadGroupDialog,
    IconBase,
    IconHose,
    IconWood,
    IconCold,
    CompanyName,
    VFloatField,
    VEditLoadDetails,
    VBooked,
    VDispatchRequest,
    VLoadNavList,
    LoadMap,
    Toolbar,
  },
  filters: {
    formatTimeStamp(momentDate) {
      return momentDate.format('HH:mm A MMM DD');
    },
  },
  beforeRouteLeave(to, from, next) {
    // Before leave save id in local storage
    const sectionKey = `${from.name}_repeatLoadId`;
    if (this.repeatLoadId) {
      localStorage.setItem(sectionKey, this.repeatLoadId);
    } else {
      localStorage.removeItem(sectionKey);
    }
    next();
  },
  computed: {
    ...mapGetters('repeatingLoadTemplates', {
      loadingLoadTemplates: 'loadingLoads',
      loadTemplatesTime: 'timestamp',
      loadTemplates: 'list',
      storeCompanyId: 'companyId',
    }),
    _() {
      return _;
    },
    loadTemplatesAccurateSince() {
      if (this.loadingLoadTemplates) {
        return 'Loading ...';
      }

      return `Updated ${this.$options.filters.formatTimeStamp(this.loadTemplatesTime)}`;
    },
    isProducerWithPermissions() {
      return this.isLtnAdmin || Checks.accountHasOfficePermission(this.producerAccount);
    },
    producerAccount() {
      return this.findCompanyAccount()(this.company.id);
    },
    breadcrumbItems() {
      return [
        {
          text: 'Loads',
          disabled: false,
          replace: true,
          to: `/companies/${this.company.id}/loads`,
        },
        {
          text: _.get(this.currentLoad, 'listingHumanId', ''),
          disabled: true,
        },
      ];
    },
    invalidLoadRate() {
      return !this.billableAmount;
    },
    billableAmount() {
      return _.get(this.planningLoad, 'billableAmount');
    },
    loadedMiles() {
      return _.get(this.planningLoad, 'loadGroup.details.loadedMiles', 'not this time');
    },
    loadedDistanceBilledRate() {
      return this.distanceRate(this.billableAmount, this.loadedMiles);
    },
    repeatingLoads() {
      const visible = this.sponsoredLoadTemplates.filter(load => this.filterLoadTemplate(load));
      const sort = this.sortDesc ? 'desc' : 'asc';
      return _.orderBy(visible, [load =>
        moment(load.pickupTime, moment.ISO_8601).unix()], [sort]);
    },
    sponsoredLoadTemplates() {
      return this.loadTemplates.map((load) => {
        const loadGroup = this.loadGroupFor(load);
        this.sponsorLoadGroup(loadGroup);
        return load;
      });
    },
    groupedBySponsorLoads() {
      const nameSorted = _.sortBy(this.sponsoredLoadTemplates, load => this.loadGroupSponsorName(load));
      return _.groupBy(nameSorted, load => this.loadGroupSponsorName(load));
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    ...mapGetters('profiles', [
      'findCompanyAccount',
    ]),
    saveDetailsUpdates() {
      this.$bus.$emit('saveEditRepeatLoad');
    },
    async openCopyLoadDialog(load) {
      if (load) {
        await this.selectCurrentLoad(load);
      }
      this.showCopyLoadDialog = true;
    },
    toggleEdition() {
      this.allowEdition = !this.allowEdition;
    },
    openNicknameModal() {
      this.showNicknameModal = true;
    },
    updateSavingState(newState) {
      if (this.savingLoad && this.savingLoad !== newState) {
        this.allowEdition = false;
      }

      this.savingLoad = newState;
    },
    async remove(load) {
      const confirm = await this.$confirm('Do you want to delete this template?', {
        color: 'warning',
        icon: 'warning',
      });

      if (confirm) {
        this.$store.dispatch('repeatingLoadTemplates/deleteLoadTemplate', load.humanId);

        if (this.isSelectedLoad(load)) {
          this.currentLoad = null;
        }
      }
    },
    closeCopyLoadDialog(load) {
      this.showCopyLoadDialog = false;

      if (load) {
        this.$router.push({
          name: 'company.loads.planner',
          params: {
            id: this.company.id,
            loadGroupId: load.humanId,
          },
        });
      }
    },
    updateRepeatLoadById(list, loadId) {
      if (list && list.length && loadId) {
        // Update planningLoad
        const selectedIndex = _.findIndex(list, load => load.humanId === loadId);
        this.selectCurrentLoad(selectedIndex > -1 ? list[selectedIndex] : null);
      }
    },
    getLoadTemplates() {
      this.$store.dispatch('repeatingLoadTemplates/getRepeatingLoadTemplates', this.company.id);
    },
    editionFormChange(isDirty) {
      this.isFormDirty = isDirty;
    },
    updateLoad(updatedLoad) {
      if (updatedLoad.humanId === this.currentLoad.humanId) {
        this.currentLoad = updatedLoad;
      }
      this.$store.dispatch('repeatingLoadTemplates/updateLoadTemplate', updatedLoad);
    },
    toggleSort() {
      this.sortDesc = !this.sortDesc;
    },
    countUnassignedTruck(loads) {
      return _.sumBy(loads, l => this.loadGroupAvailableLoadCount(l));
    },
    checkPendingChanges() {
      if (this.isFormDirty) {
        return this.$confirm('You have unsaved changes. Do you want to continue?', {
          color: 'warning',
          icon: 'warning',
        });
      }

      return true;
    },
    async selectCurrentLoad(load) {
      const confirm = await this.checkPendingChanges();
      if (confirm) {
        this.$set(this, 'currentLoad', load);
      }
    },
    isSelectedLoad(load) {
      return load.humanId === _.get(this.currentLoad, 'humanId', null);
    },
    filterLoadTemplate(load) {
      return this.filterLoadGroup(load) ||
        this.filter(load.humanId, this.search) ||
        this.filter(load.nickname, this.search);
    },
    formatCompaniesList(companies) {
      // eslint-disable-next-line
      return companies.map((company) => {
        return {
          companyDisplayName: company.companyName,
          companyId: company.id,
        };
      });
    },
    activeBookedTruckCount(load) {
      const booked = _.get(load, 'bookedLoads', []);
      return _.filter(booked, b => !this.isBookedLoadCanceled(b)).length;
    },
    onRefresh() {
      this.getLoadTemplates();
      if (this.currentLoad) {
        this.refreshLoad();
      }
    },
    async refreshLoad() {
      try {
        this.loadingLoad = true;
        this.$store.commit(`applications/${types.START_LOADING}`);
        const apiResponse = await API.company.getRepeatLoad(this.currentLoad.humanId);
        this.updateLoad(apiResponse.data);
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingLoad = false;
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
  },
  created() {
    if (this.storeCompanyId !== this.company.id) {
      this.getLoadTemplates();
    }

    const sectionKey = `${this.$route.name}_repeatLoadId`;

    if (this.selectedLoad) {
      this.currentLoad = this.selectedLoad;
    } else if (this.repeatLoadId || localStorage.getItem(sectionKey)) {
      this.updateRepeatLoadById(this.repeatingLoads,
        this.repeatLoadId || localStorage.getItem(sectionKey));
    }
  },
};
</script>

<style scoped>
  .gray-tile {
    background-color: #88a2aa;
  }

  .imminent {
    color: #E53935;
  }

  .sort-btn {
    cursor: pointer;
  }

  .side-list-desktop {
    height: calc(100vh - 310px);
    overflow: auto;
  }

  .main-area-desktop {
    max-height: calc(100vh - 250px);
    overflow: auto;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Companies/Loads/components/RepeatLoadTemplatesTool.vue